import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  FiTrendingUp,
  FiUsers,
  FiBookOpen,
  FiClock,
  FiTarget,
  FiDownload,
  FiChevronRight
} from 'react-icons/fi';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { analyticsAPI } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';

const Analytics = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('30d');
  const { showError } = useNotification();

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      const response = await analyticsAPI.getDashboardStats();
      setAnalytics(response?.data?.data || response?.data);
    } catch (error) {
      showError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [showError]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics, timeRange]);

  const handleExportData = async () => {
    try {
      const rows = [];
      const dateStr = new Date().toISOString().split('T')[0];
      if (analytics?.stats && typeof analytics.stats === 'object') {
        rows.push(['metric', 'value']);
        Object.entries(analytics.stats).forEach(([key, value]) => { rows.push([key, value]); });
      } else {
        rows.push(['metric', 'value']);
        rows.push(['totalStudents', analytics?.totalStudents ?? 0]);
        rows.push(['studentGrowth', analytics?.studentGrowth ?? 0]);
        rows.push(['totalSessions', analytics?.totalSessions ?? 0]);
        rows.push(['sessionGrowth', analytics?.sessionGrowth ?? 0]);
        rows.push(['averageScore', analytics?.averageScore ?? 0]);
        rows.push(['scoreImprovement', analytics?.scoreImprovement ?? 0]);
        rows.push(['totalHours', analytics?.totalHours ?? 0]);
        rows.push(['hoursGrowth', analytics?.hoursGrowth ?? 0]);
      }
      const csv = rows.map((r) => r.map((v) => (typeof v === 'string' ? `"${String(v).replace(/"/g, '""')}"` : v)).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `analytics-${timeRange}-${dateStr}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      showError('Failed to export analytics data');
    }
  };

  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
  };

  const formatPercentage = (value) => `${(value || 0).toFixed(1)}%`;

  const getTimeRangeLabel = (range) => {
    const labels = { '7d': 'Last 7 days', '30d': 'Last 30 days', '90d': 'Last 3 months', '1y': 'Last year' };
    return labels[range] || range;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Analytics Dashboard</h1>
            <p className="text-sm text-slate-500 mt-0.5">Track performance, monitor progress, and gain insights into learning patterns.</p>
          </div>
          <div className="flex items-center gap-2">
            <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-slate-400/30 focus:border-slate-400">
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 3 months</option>
              <option value="1y">Last year</option>
            </select>
            <button type="button" onClick={handleExportData} className="inline-flex items-center gap-2 px-4 py-2.5 border border-slate-200 bg-white text-slate-700 text-sm font-medium rounded-lg hover:bg-slate-50 transition-colors">
              <FiDownload className="h-4 w-4" />
              Export Data
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><FiUsers className="h-5 w-5" /></div>
              <div>
                <div className="text-2xl font-semibold text-slate-900">{formatNumber(analytics?.totalStudents)}</div>
                <div className="text-sm text-slate-500">Total Students</div>
                <div className="text-xs text-emerald-600 mt-0.5">+{formatPercentage(analytics?.studentGrowth)} from last period</div>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><FiBookOpen className="h-5 w-5" /></div>
              <div>
                <div className="text-2xl font-semibold text-slate-900">{formatNumber(analytics?.totalSessions)}</div>
                <div className="text-sm text-slate-500">Sessions Completed</div>
                <div className="text-xs text-emerald-600 mt-0.5">+{formatPercentage(analytics?.sessionGrowth)} from last period</div>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><FiTarget className="h-5 w-5" /></div>
              <div>
                <div className="text-2xl font-semibold text-slate-900">{formatPercentage(analytics?.averageScore)}</div>
                <div className="text-sm text-slate-500">Average Score</div>
                <div className="text-xs text-emerald-600 mt-0.5">+{formatPercentage(analytics?.scoreImprovement)} improvement</div>
              </div>
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600"><FiClock className="h-5 w-5" /></div>
              <div>
                <div className="text-2xl font-semibold text-slate-900">{(analytics?.totalHours ?? 0)}h</div>
                <div className="text-sm text-slate-500">Study Hours</div>
                <div className="text-xs text-emerald-600 mt-0.5">+{formatPercentage(analytics?.hoursGrowth)} from last period</div>
              </div>
            </div>
          </div>
        </div>

        <div className="mb-8">
          <h2 className="text-sm font-medium text-slate-900 mb-3">Detailed Reports</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Link to="/admin/performance" className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 flex items-start gap-3 hover:border-slate-300 hover:bg-slate-50/50 transition-colors">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0"><FiTarget className="h-5 w-5" /></div>
              <div className="min-w-0">
                <h3 className="font-medium text-slate-900">Performance Analysis</h3>
                <p className="text-sm text-slate-500 mt-0.5">Detailed breakdown of student performance across subjects and topics.</p>
              </div>
              <FiChevronRight className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
            </Link>
            <Link to="/admin/progress" className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 flex items-start gap-3 hover:border-slate-300 hover:bg-slate-50/50 transition-colors">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0"><FiTrendingUp className="h-5 w-5" /></div>
              <div className="min-w-0">
                <h3 className="font-medium text-slate-900">Progress Tracking</h3>
                <p className="text-sm text-slate-500 mt-0.5">Monitor learning progress and identify improvement areas.</p>
              </div>
              <FiChevronRight className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
            </Link>
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 flex items-start gap-3 opacity-90">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0"><FiBookOpen className="h-5 w-5" /></div>
              <div className="min-w-0">
                <h3 className="font-medium text-slate-900">Subject Distribution</h3>
                <p className="text-sm text-slate-500 mt-0.5">View time and effort distribution across different subjects.</p>
              </div>
            </div>
            <Link to="/admin/activity" className="rounded-lg border border-slate-200 bg-white shadow-sm p-4 flex items-start gap-3 hover:border-slate-300 hover:bg-slate-50/50 transition-colors">
              <div className="h-10 w-10 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0"><FiClock className="h-5 w-5" /></div>
              <div className="min-w-0">
                <h3 className="font-medium text-slate-900">Activity Timeline</h3>
                <p className="text-sm text-slate-500 mt-0.5">Track daily and weekly learning activities and patterns.</p>
              </div>
              <FiChevronRight className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
            </Link>
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden mb-8">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/80 flex items-center justify-between">
            <h3 className="text-sm font-medium text-slate-900">Top Performers ({getTimeRangeLabel(timeRange)})</h3>
            <Link to="/admin/performance" className="text-sm font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
              View All <FiChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="p-4">
            {analytics?.topPerformers?.length ? (
              <ul className="divide-y divide-slate-100">
                {analytics.topPerformers.map((performer, index) => (
                  <li key={index} className="py-3 flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold shrink-0 ${index === 0 ? 'bg-amber-100 text-amber-800' : index === 1 ? 'bg-slate-200 text-slate-700' : index === 2 ? 'bg-amber-50 text-amber-700' : 'bg-slate-100 text-slate-600'}`}>#{index + 1}</span>
                    <div className="h-9 w-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-700 shrink-0">
                      {performer.name && performer.name.split(' ').map((n) => n[0]).join('')}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900">{performer.name}</div>
                      <div className="text-xs text-slate-500">{performer.testsCompleted} tests · {formatPercentage(performer.averageScore)} avg</div>
                    </div>
                    <div className="font-semibold text-slate-900 shrink-0">{formatPercentage(performer.averageScore)}</div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500 py-4">No top performers available for this range.</p>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 bg-slate-50/80 flex items-center justify-between">
            <h3 className="text-sm font-medium text-slate-900">Recent Activity</h3>
            <Link to="/admin/activity" className="text-sm font-medium text-slate-700 hover:text-slate-900 inline-flex items-center gap-1">
              View All <FiChevronRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="p-4">
            {analytics?.recentActivity?.length ? (
              <ul className="divide-y divide-slate-100">
                {analytics.recentActivity.map((activity, index) => (
                  <li key={index} className="py-3 flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-slate-100 flex items-center justify-center text-slate-600 shrink-0">
                      {activity.type === 'test' && <FiTarget className="h-4 w-4" />}
                      {activity.type === 'session' && <FiBookOpen className="h-4 w-4" />}
                      {activity.type === 'game' && <FiUsers className="h-4 w-4" />}
                      {!['test', 'session', 'game'].includes(activity.type) && <FiBookOpen className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-slate-900">{activity.title}</div>
                      <div className="text-xs text-slate-500">{activity.subject}</div>
                      <div className="text-xs text-slate-400 mt-0.5">
                        {activity.date && new Date(activity.date).toLocaleDateString()}
                        {activity.score != null && ` · Score: ${activity.score}%`}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-slate-500 py-4">No recent activity in this range.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
